import React from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import {
  getCompletionStatusStyles,
  getSecondaryLanguages,
  LANGUAGE_LABELS,
} from "./languageConfig";

export const LanguageChips = ({
  languages,
  languageCompletionByCode = {},
  onLanguageClick,
  align = "left",
}) => {
  if (!languages.length) {
    return null;
  }

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: align === "center" ? "center" : "flex-start",
        width: "max-content",
      }}
    >
      {languages.map((language) => (
        <Button
          key={language}
          size="small"
          style={{
            ...getCompletionStatusStyles(languageCompletionByCode[language]),
            flexShrink: 0,
            marginRight: 8,
          }}
          variant="contained"
          onClick={() => onLanguageClick(language)}
        >
          {LANGUAGE_LABELS[language] || language.toUpperCase()}
        </Button>
      ))}
    </div>
  );
};

export const PinnedLanguageField = ({
  validLanguages,
  value,
  onChange,
  label = "Pinned language",
  fullWidth = true,
  disabled = false,
  requireSetConfirmation = false,
  onConfirm,
  inlineLabel = "Select default language",
  showInlineLabel = false,
  dropdownMinWidth = 180,
}) => {
  const field = (
    <TextField
      select
      InputLabelProps={{ shrink: true }}
      disabled={disabled || validLanguages.length === 1}
      fullWidth={fullWidth && !showInlineLabel}
      label={showInlineLabel ? undefined : label}
      margin="none"
      size="small"
      style={showInlineLabel ? { minWidth: dropdownMinWidth } : undefined}
      value={value}
      variant="outlined"
      onChange={(event) => onChange(event.target.value)}
    >
      {validLanguages.map((language) => (
        <MenuItem key={language} value={language}>
          {LANGUAGE_LABELS[language] || language.toUpperCase()}
        </MenuItem>
      ))}
    </TextField>
  );

  if (!showInlineLabel && !requireSetConfirmation) {
    return field;
  }

  return (
    <Box alignItems="center" display="flex" style={{ minHeight: 40 }}>
      {showInlineLabel && (
        <Typography style={{ marginRight: "0.75rem" }} variant="body2">
          {inlineLabel}
        </Typography>
      )}
      {field}
      {requireSetConfirmation && (
        <Box ml={1}>
          <Button
            color="primary"
            disabled={disabled}
            variant="contained"
            onClick={onConfirm}
          >
            SET
          </Button>
        </Box>
      )}
    </Box>
  );
};

const LanguageSelector = ({
  validLanguages,
  preferredLanguage,
  languageCompletionByCode = {},
  onPreferredLanguageChange,
  onActiveSecondaryLanguageChange,
  layout = "stacked",
  align = "left",
  dropdownLabel = "Pinned language",
  dropdownMaxWidth = 240,
  disabled = false,
  requireSetConfirmation = false,
  pendingPreferredLanguage,
  onPendingPreferredLanguageChange,
  onConfirmPreferredLanguage,
  showInlineLabel = false,
  inlineLabel = "Select default language",
  style,
}) => {
  const secondaryLanguages = getSecondaryLanguages(
    validLanguages,
    preferredLanguage
  );
  const dropdownValue = requireSetConfirmation
    ? pendingPreferredLanguage
    : preferredLanguage;

  const handlePreferredChange = (nextPreferred) => {
    if (requireSetConfirmation) {
      onPendingPreferredLanguageChange(nextPreferred);
      return;
    }

    const nextSecondary =
      getSecondaryLanguages(validLanguages, nextPreferred)[0] || null;
    onPreferredLanguageChange(nextPreferred, nextSecondary);
  };

  const chips = (
    <LanguageChips
      align={align}
      languageCompletionByCode={languageCompletionByCode}
      languages={secondaryLanguages}
      onLanguageClick={onActiveSecondaryLanguageChange}
    />
  );

  if (layout === "split") {
    return (
      <>
        <Grid item md={secondaryLanguages.length ? 6 : 12} xs={12}>
          <PinnedLanguageField
            disabled={disabled}
            label={dropdownLabel}
            validLanguages={validLanguages}
            value={preferredLanguage}
            onChange={(nextPreferred) => {
              const nextSecondary =
                getSecondaryLanguages(validLanguages, nextPreferred)[0] || null;
              onPreferredLanguageChange(nextPreferred, nextSecondary);
            }}
          />
        </Grid>
        {secondaryLanguages.length > 0 && (
          <Grid item md={6} xs={12}>
            {chips}
          </Grid>
        )}
      </>
    );
  }

  if (layout === "inline") {
    return (
      <div style={style}>
        <PinnedLanguageField
          disabled={disabled}
          inlineLabel={inlineLabel}
          requireSetConfirmation={requireSetConfirmation}
          showInlineLabel={showInlineLabel}
          validLanguages={validLanguages}
          value={dropdownValue}
          onChange={handlePreferredChange}
          onConfirm={onConfirmPreferredLanguage}
        />
      </div>
    );
  }

  const containerStyle = {
    marginBottom: "1.5rem",
    ...(align === "center"
      ? {
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }
      : {}),
    ...style,
  };

  return (
    <div style={containerStyle}>
      <div
        style={{
          maxWidth: dropdownMaxWidth,
          width: align === "center" ? "100%" : undefined,
        }}
      >
        <PinnedLanguageField
          disabled={disabled}
          label={dropdownLabel}
          requireSetConfirmation={requireSetConfirmation}
          validLanguages={validLanguages}
          value={dropdownValue}
          onChange={handlePreferredChange}
          onConfirm={onConfirmPreferredLanguage}
        />
      </div>
      {secondaryLanguages.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: align === "center" ? "center" : "flex-start",
            marginTop: 12,
            width: align === "center" ? "100%" : undefined,
          }}
        >
          {chips}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
